
import { Component, Prop, Vue, Emit } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { BackButton } from '@/modules/common/components/buttons/BackButton';
import {
  NavigationPanelWrapper,
  NavigationPanel
} from '@/modules/common/components/NavigationPanel';
import { WatchListInfiniteScroll } from './components/WatchListInfiniteScroll';
import { GeneralSidebarList } from './components/GeneralSidebarList';
import { SummaryList, SummaryTypes } from './components/SummaryList';
import { SubscriptionList } from './components/SubscriptionList';
import { EventsProvisionedList } from './components/EventsProvisionedList';
import { SidebarTypes } from './enums';
import { BlockedBalance } from './components/BlockedBalance';

import {
  TradePosition,
  GetPositionSubscriptionDocument,
  TradePositionPrice
} from '@/modules/common/services/graphql/server/server.sdk';
import { Instrument as InstrumentWithWatchlist } from '@/modules/trade/services/search';
import { IPlansProductData } from '@/modules/trade/services/store';
import { ObservableSubscription } from '@apollo/client/utilities/observables/Observable';

import { SignalR, MarketData } from '@/modules/trade/services/signalr';
import {
  AccountStatus,
  Balances,
  HomeBrokerStatus
} from '@/modules/trade/services/account';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';

interface PositionSubcriptionData {
  tradePositionsPrice: TradePositionPrice[] | null;
}

const customerModule = namespace('CustomerModule');
const tradePositionsModule = namespace('tradeModule');

@Component({
  components: {
    BackButton,
    NavigationPanel,
    NavigationPanelWrapper,
    WatchListInfiniteScroll,
    EventsProvisionedList,
    GeneralSidebarList,
    SubscriptionList,
    SummaryList,
    BlockedBalance
  }
})
export default class TradeSidebar extends Vue {
  @Prop({ type: Object, required: false })
  readonly balances?: Balances;

  @Prop({ type: Object, required: false })
  readonly tradeStatus?: AccountStatus;

  @Prop({ type: Function, default: () => {} })
  readonly onAccessHomeBrokerClick!: Function;

  @Prop({ type: Function, default: () => {} })
  readonly openBrokerModal!: Function;

  @Prop({ type: Function, default: () => {} })
  readonly openTradeMapModal!: Function;

  @Prop({ type: Object, default: null })
  readonly homeBrokerStatus!: HomeBrokerStatus | null;

  @Prop({ type: Array, required: true })
  readonly customerProducts!: IPlansProductData[];

  @tradePositionsModule.Getter('getPositions')
  readonly positions!: TradePosition[];

  @tradePositionsModule.Getter('getWatchlist')
  readonly watchlist!: InstrumentWithWatchlist[];

  @tradePositionsModule.Mutation('setPositionSubscription')
  readonly setPositionSubscription!: (
    subscription: ObservableSubscription
  ) => void;

  @tradePositionsModule.Mutation('updateWatchlist')
  readonly updateWatchlist!: (data: MarketData) => void;

  @tradePositionsModule.Mutation('updatePosition')
  readonly updatePosition!: (data: TradePositionPrice) => void;

  @customerModule.Getter('getCustomerId') readonly customerId!: string;

  private sidebarTypes = SidebarTypes;
  private summaryTypes = SummaryTypes;
  private isWatchlistInvoked = false;

  @Emit('handle-blocked-balance-modal')
  handleBlockedBalanceModal() {}

  created() {
    if (!this.isFinOneIntegrationWithTradeV0Enabled) this.startSignalR();
  }

  destroyed() {
    this.stopSignalR();
  }

  get isFinOneIntegrationWithTradeV0Enabled() {
    return FeatureFlagsModule.isEnabled(
      FeatureFlags.ENABLE_FIN_ONE_INTEGRATION_WITH_TRADE_V0
    );
  }

  get blockedBalance() {
    return this.balances?.blockedBalance;
  }

  private async startSignalR() {
    await SignalR.startConnection();

    SignalR.connection.on('MarketData', (data: MarketData) => {
      this.updateWatchlist(data);
    });

    SignalR.connection.onreconnected(async () => {
      this.isWatchlistInvoked = false;

      await this.initiateWatchListSubscription();
    });
  }

  private async stopSignalR() {
    await SignalR.stopConnection();
  }

  async initiatePositionSubscription() {
    if (Array.isArray(this.positions) && this.positions.length !== 0) {
      this.subscribeToPositions();
    }
  }

  private subscribeToPositions() {
    const observer = this.$apollo.subscribe({
      query: GetPositionSubscriptionDocument
    });
    const subscription = observer.subscribe({
      next: ({ data }: { data: PositionSubcriptionData }) =>
        this.positionSubscriptionCallback(data)
    });

    this.setPositionSubscription(subscription);
  }

  private positionSubscriptionCallback(data: PositionSubcriptionData) {
    const currentPositions = this.positions;

    if (Array.isArray(data.tradePositionsPrice)) {
      data.tradePositionsPrice.forEach((position: TradePositionPrice) => {
        if (Array.isArray(currentPositions)) {
          this.updatePosition(position);
        }
      });
    }
  }

  async initiateWatchListSubscription() {
    if (Array.isArray(this.watchlist) && this.watchlist.length !== 0) {
      if (!this.isWatchlistInvoked) {
        const watchlistSymbols = this.watchlist.map(
          watchlist => watchlist.symbol
        );
        await SignalR.invokeWithSubscription('JoinGroup', watchlistSymbols);
        this.isWatchlistInvoked = true;
      }
    }
  }
}
